import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import {
  Table, Modal, Form, Input, InputNumber, Select, message,
  Popconfirm, Button, Space,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getIndicatorList, createIndicator, updateIndicator, deleteIndicator, toggleIndicatorUse,
  getIndicatorGroupSelect,
} from "../api/domainApi";
import { IndicatorRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const PAGE_SIZE = 20;

const IndicatorPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [filterGrpId, setFilterGrpId] = useState<number | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IndicatorRes | null>(null);
  const [form] = Form.useForm();

  const swrKey = `/indicator?page=${page}&kw=${keyword}&grp=${filterGrpId}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () => getIndicatorList({ page: page - 1, size: PAGE_SIZE, keyword, indicatorGrpId: filterGrpId }),
    { keepPreviousData: true }
  );

  const { data: groups } = useSWR("/indicator-group/select", getIndicatorGroupSelect);

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (row: IndicatorRes) => { setEditing(row); form.setFieldsValue(row); setModalOpen(true); };

  const handleOk = async () => {
    const values = await form.validateFields();
    try {
      if (editing) {
        await updateIndicator(editing.id, values);
        message.success("수정되었습니다.");
      } else {
        await createIndicator(values);
        message.success("추가되었습니다.");
      }
      setModalOpen(false);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteIndicator(id);
      message.success("삭제되었습니다.");
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<IndicatorRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "그룹", dataIndex: "grpName", width: 100 },
    { title: "축", dataIndex: "axisType", width: 50 },
    { title: "코드", dataIndex: "code", width: 80 },
    { title: "한글명", dataIndex: "nameKo", ellipsis: { showTitle: true } },
    { title: "영문명", dataIndex: "nameEn", ellipsis: { showTitle: true } },
    { title: "순서", dataIndex: "sortOrder", width: 60 },
    { title: "사용여부", dataIndex: "useYn", width: 90, render: (v) => <UseYnTag value={v} /> },
    {
      title: "관리",
      width: 220,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>수정</Button>
          <Button size="small" type="dashed" onClick={() => navigate(`/indicators/${row.id}`)}>상세</Button>
          <Button size="small" onClick={async () => {
            try { await toggleIndicatorUse(row.id); mutate(); }
            catch (e: any) { message.error(e.message); }
          }}>
            {row.useYn ? "비활성" : "활성"}
          </Button>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="인디케이터" onAdd={openCreate} />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="그룹 선택" allowClear style={{ width: 180 }}
          options={groups?.map((g) => ({ value: g.id, label: g.grpName }))}
          onChange={(v) => { setFilterGrpId(v); setPage(1); }}
        />
        <Input placeholder="한글명/영문명 검색" value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => { setKeyword(kwInput); setPage(1); }}
          style={{ width: 220 }}
        />
        <Button onClick={() => { setKeyword(kwInput); setPage(1); }}>검색</Button>
        <Button onClick={() => { setKwInput(""); setKeyword(""); setFilterGrpId(undefined); setPage(1); }}>초기화</Button>
      </Space>
      <Table
        rowKey="id" columns={columns} dataSource={data?.content} loading={isLoading}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.page?.totalElements, onChange: setPage, showSizeChanger: false }}
        size="small"
      />

      <Modal
        title={editing ? "인디케이터 수정" : "인디케이터 추가"}
        open={modalOpen} onOk={handleOk} onCancel={() => setModalOpen(false)}
        okText={editing ? "수정" : "추가"} cancelText="취소"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="indicatorGrpId" label="그룹" rules={[{ required: true }]}>
            <Select options={groups?.map((g) => ({ value: g.id, label: g.grpName }))} />
          </Form.Item>
          <Form.Item name="code" label="지표 코드" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="nameKo" label="한글명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="nameEn" label="영문명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="sortOrder" label="정렬 순서" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default IndicatorPage;
